import { AlertTriangle, Download, Image, Lock, MessageCircle, Moon, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { billingService } from '@/services/billing';
import { userService } from '@/services/users';
import { backupService, type BackupData, type ImportResult } from '@/services/backup';
import { integrationService } from '@/services/integrations';
import { useAuth } from '@/hooks/useAuth';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasPendingCancellation?: boolean;
  onCancellationRequest?: () => void;
}

export const SettingsModal = ({
  isOpen,
  onClose,
  hasPendingCancellation = false,
  onCancellationRequest,
}: SettingsModalProps) => {
  const { user, logout } = useAuth();
  const isDemo = user?.email === 'demo@pedidoo.com';
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [hasRequestedCancellation, setHasRequestedCancellation] =
    useState(hasPendingCancellation);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({ orders: true, clients: true, products: true });
  const [importPassword, setImportPassword] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importData, setImportData] = useState<BackupData | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document.documentElement.classList.contains('dark'),
  );
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  useEffect(() => {
    setHasRequestedCancellation(hasPendingCancellation);
  }, [hasPendingCancellation]);

  useEffect(() => {
    if (isOpen && (user?.role === 'ADMIN_CLI' || user?.role === 'SUPERADMIN')) {
      integrationService.getWhatsAppConfig()
        .then((cfg) => {
          setWhatsappPhone(cfg.phone ?? '');
          setWhatsappEnabled(cfg.whatsappEnabled);
        })
        .catch(() => {});
    }
  }, [isOpen, user?.role]);

  useEffect(() => {
    if (isOpen && user?.role === 'ADMIN_CLI') {
      // Carregar logo atual ao abrir o modal
      userService
        .getLogo()
        .then((data) => {
          const logoUrl = data.logoUrl;
          setCurrentLogoUrl(logoUrl);
          if (logoUrl) {
            // Construir URL completa do logo (mesmo formato usado nos PDFs)
            const normalizedLogoUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
            const fullLogoUrl = `${import.meta.env.VITE_API_URL || ''}${normalizedLogoUrl}`;
            setLogoPreview(fullLogoUrl);
          } else {
            setCurrentLogoUrl(null);
            setLogoPreview(null);
          }
        })
        .catch(() => {
          // Ignorar erro se não houver logo
          setCurrentLogoUrl(null);
          setLogoPreview(null);
        });
    } else if (!isOpen) {
      // Resetar preview quando fechar o modal, mas manter currentLogoUrl
      setLogoFile(null);
    }
  }, [isOpen, user?.role]);

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (!user?.id) {
      toast.error('Usuário não identificado');
      return;
    }

    setIsChangingPassword(true);
    try {
      await userService.updatePassword(user.id, newPassword);
      toast.success('Senha alterada com sucesso. Você será deslogado.');
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setIsChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleToggleDarkMode = async () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    if (next) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('pedidoo:darkMode', String(next));
    try {
      await userService.setDarkMode(next);
    } catch {
      // Preferência local já foi salva; não reverter
    }
  };

  const handleSaveWhatsApp = async () => {
    setWhatsappLoading(true);
    try {
      const updated = await integrationService.updateWhatsAppConfig({
        phone: whatsappPhone,
        whatsappEnabled,
      });
      setWhatsappPhone(updated.phone ?? '');
      setWhatsappEnabled(updated.whatsappEnabled);
      toast.success('Configurações de WhatsApp salvas!');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleRequestCancellation = async () => {
    if (!user?.id) {
      toast.error('Usuário não identificado');
      return;
    }

    setIsCancelling(true);
    try {
      await billingService.requestCancellation();
      setHasRequestedCancellation(true);
      onCancellationRequest?.();
      toast.success('Solicitação enviada. Um administrador analisará em breve.');
      setShowCancelDialog(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao solicitar cancelamento');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    // Validar tamanho (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2MB');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const result = await userService.uploadLogo(logoFile);
      const logoUrl = result.logoUrl;
      setCurrentLogoUrl(logoUrl); // Manter o logoUrl após upload
      // Construir URL completa do logo (mesmo formato usado nos PDFs)
      const normalizedLogoUrl = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
      const fullLogoUrl = `${import.meta.env.VITE_API_URL || ''}${normalizedLogoUrl}`;
      setLogoPreview(fullLogoUrl);
      setLogoFile(null);
      toast.success('Logo atualizado com sucesso!');
      // Invalidar apenas queries relacionadas ao logo (não invalidar user para evitar problemas)
      queryClient.invalidateQueries({ queryKey: ['logo'] });
      // Forçar atualização do logo em toda a aplicação
      window.dispatchEvent(new CustomEvent('logo-updated'));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao fazer upload do logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setIsUploadingLogo(true);
    try {
      await userService.removeLogo();
      setCurrentLogoUrl(null);
      setLogoPreview(null);
      setLogoFile(null);
      toast.success('Logo removido. Voltando ao logo padrão do Pedidoo.');
      // Invalidar apenas queries relacionadas ao logo (não invalidar user para evitar problemas)
      queryClient.invalidateQueries({ queryKey: ['logo'] });
      // Forçar atualização do logo em toda a aplicação
      window.dispatchEvent(new CustomEvent('logo-updated'));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao remover logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      const blob = await backupService.export(exportOptions);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Backup exportado com sucesso!');
      setShowExportModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao exportar backup');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Apenas arquivos JSON são permitidos');
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text) as BackupData;
      
      // Validar estrutura básica
      if (!data.version || !data.exportedAt) {
        toast.error('Formato de backup inválido');
        return;
      }

      setImportFile(file);
      setImportData(data);
      setShowImportConfirm(true);
    } catch (error) {
      toast.error('Erro ao ler arquivo. Verifique se é um JSON válido.');
    }
  };

  const handleImportBackup = async () => {
    if (!importPassword || !importData) {
      toast.error('Preencha a senha e selecione um arquivo');
      return;
    }

    setIsImporting(true);
    try {
      const result = await backupService.import(importPassword, importData);
      
      // Oferecer download do backup automático
      const blob = new Blob([result.autoBackup], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-auto-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(
        `Backup importado com sucesso! ${result.imported.orders} pedidos, ${result.imported.clients} clientes, ${result.imported.products} produtos. Um backup automático foi baixado.`
      );
      
      // Invalidar todas as queries para recarregar dados
      queryClient.invalidateQueries();
      
      setShowImportModal(false);
      setShowImportConfirm(false);
      setImportPassword('');
      setImportFile(null);
      setImportData(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao importar backup');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configurações" wide={false}>
      {isDemo && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">Modo demo</span> — configurações de conta não estão disponíveis na demonstração.
          Crie uma conta grátis para acessar todas as funcionalidades.
        </div>
      )}
      <div className="space-y-6">
        {/* Alterar Senha */}
        {!isDemo && <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Lock className="size-5 text-slate-600" />
            <div className="flex-1">
              <h3 className="font-medium text-slate-900">Alterar Senha</h3>
              <p className="text-sm text-slate-500">Altere sua senha de acesso</p>
            </div>
          </div>
          <div className="space-y-3 pl-8">
            <Input
              type="password"
              placeholder="Nova senha (mínimo 8 caracteres)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Confirmar nova senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              onClick={handleChangePassword}
              disabled={isChangingPassword || !newPassword || !confirmPassword}
              className="w-full"
            >
              {isChangingPassword ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </div>}

        {/* Aparência */}
        <div className="space-y-3 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-3">
            <Moon className="size-5 text-slate-600" />
            <div className="flex-1">
              <h3 className="font-medium text-slate-900">Aparência</h3>
              <p className="text-sm text-slate-500">Alterna entre modo claro e escuro no sistema</p>
            </div>
          </div>
          <div className="pl-8">
            <button
              type="button"
              onClick={handleToggleDarkMode}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                isDarkMode ? 'bg-primary-600' : 'bg-slate-200'
              }`}
              role="switch"
              aria-checked={isDarkMode}
            >
              <span
                className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="ml-3 text-sm text-slate-600">
              {isDarkMode ? 'Modo escuro ativado' : 'Modo claro'}
            </span>
          </div>
        </div>

        {/* Upload de Logo - Apenas para ADMIN_CLI */}
        {!isDemo && user?.role === 'ADMIN_CLI' && (
          <div className="space-y-3 border-t border-slate-200 pt-6">
            <div className="flex items-center gap-3">
              <Image className="size-5 text-slate-600" />
              <div className="flex-1">
                <h3 className="font-medium text-slate-900">Logotipo da Empresa</h3>
                <p className="text-sm text-slate-500">
                  Faça upload do logotipo da sua empresa. Ele será exibido nos PDFs.
                </p>
              </div>
            </div>
            <div className="space-y-3 pl-8">
              {(logoPreview || currentLogoUrl) && (
                <div className="flex items-center gap-4">
                  <img
                    src={logoPreview || (currentLogoUrl ? `${import.meta.env.VITE_API_URL || ''}${currentLogoUrl.startsWith('/') ? currentLogoUrl : `/${currentLogoUrl}`}` : '')}
                    alt="Logo preview"
                    className="h-16 w-auto rounded border border-slate-200 object-contain bg-white"
                    onError={(e) => {
                      // Se o logo falhar ao carregar, ocultar o preview
                      const img = e.currentTarget;
                      img.style.display = 'none';
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-slate-600">Preview do logo</p>
                    <p className="text-xs text-slate-500">
                      Este é o logo que será exibido nos PDFs. Máximo 2MB. Formatos: JPG, PNG, WEBP, GIF
                    </p>
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={handleLogoFileChange}
                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
              />
              <div className="flex gap-2">
                {logoFile && (
                  <Button onClick={handleUploadLogo} disabled={isUploadingLogo} className="flex-1">
                    {isUploadingLogo ? 'Enviando...' : 'Salvar Logo'}
                  </Button>
                )}
                {currentLogoUrl && !logoFile && (
                  <Button
                    variant="danger"
                    onClick={handleRemoveLogo}
                    disabled={isUploadingLogo}
                    className="flex-1"
                  >
                    {isUploadingLogo ? 'Removendo...' : 'Limpar Logo'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Backup e Importação - Apenas para ADMIN_CLI */}
        {!isDemo && user?.role === 'ADMIN_CLI' && (
          <>
            <div className="space-y-3 border-t border-slate-200 pt-6">
              <div className="flex items-center gap-3">
                <Download className="size-5 text-slate-600" />
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">Backup e Importação</h3>
                  <p className="text-sm text-slate-500">
                    Exporte ou importe seus dados (pedidos, clientes, produtos) em formato JSON.
                  </p>
                </div>
              </div>
              <div className="space-y-3 pl-8">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowExportModal(true)}
                    className="flex-1 gap-2"
                  >
                    <Download className="size-4" />
                    Exportar Backup
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowImportModal(true)}
                    className="flex-1 gap-2"
                  >
                    <Upload className="size-4" />
                    Importar Backup
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* WhatsApp */}
        {!isDemo && (user?.role === 'ADMIN_CLI' || user?.role === 'SUPERADMIN') && (
          <div className="space-y-3 border-t border-slate-200 pt-6">
            <div className="flex items-center gap-3">
              <MessageCircle className="size-5 text-green-600" />
              <div className="flex-1">
                <h3 className="font-medium text-slate-900">WhatsApp</h3>
                <p className="text-sm text-slate-500">
                  Configure seu número e ative o recebimento de pedidos via WhatsApp
                </p>
              </div>
            </div>
            <div className="space-y-3 pl-8">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  Número do WhatsApp
                </label>
                <Input
                  placeholder="Ex: 5511999999999"
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-400">
                  Formato: código do país + DDD + número (sem espaços ou traços)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setWhatsappEnabled(!whatsappEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    whatsappEnabled ? 'bg-green-600' : 'bg-slate-200'
                  }`}
                  role="switch"
                  aria-checked={whatsappEnabled}
                >
                  <span
                    className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${
                      whatsappEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-slate-600">
                  {whatsappEnabled ? 'Pedidos via WhatsApp ativados' : 'Pedidos via WhatsApp desativados'}
                </span>
              </div>
              <Button
                onClick={handleSaveWhatsApp}
                disabled={whatsappLoading}
                className="w-full"
              >
                {whatsappLoading ? 'Salvando...' : 'Salvar configurações'}
              </Button>
            </div>
          </div>
        )}

        {/* Cancelamento */}
        {!isDemo && user?.role !== 'SUPERADMIN' && <div className="space-y-3 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="size-5 text-amber-600" />
            <div className="flex-1">
              <h3 className="font-medium text-slate-900">Cancelar mensalidade</h3>
              <p className="text-sm text-slate-500">
                Envie uma solicitação para cancelar sua assinatura. O time administrativo confirmará o
                processo e desativará o acesso.
              </p>
            </div>
          </div>
          <div className="space-y-3 pl-8">
            <Button
              variant="danger"
              disabled={hasRequestedCancellation || isCancelling}
              onClick={() => setShowCancelDialog(true)}
            >
              {hasRequestedCancellation ? 'Cancelamento solicitado' : 'Solicitar cancelamento'}
            </Button>
            {hasRequestedCancellation && (
              <p className="text-xs text-slate-500">
                Sua solicitação está em análise. Você será notificado quando houver atualização.
              </p>
            )}
          </div>
        </div>}
      </div>

      <ConfirmDialog
        isOpen={showCancelDialog}
        title="Deseja cancelar sua assinatura?"
        message="Ao confirmar, o time será alertado e seu acesso poderá ser suspenso após a análise."
        confirmText="Confirmar cancelamento"
        cancelText="Voltar"
        onConfirm={handleRequestCancellation}
        onClose={() => {
          if (!isCancelling) {
            setShowCancelDialog(false);
          }
        }}
        isLoading={isCancelling}
      />

      {/* Modal de Exportação */}
      <Modal
        isOpen={showExportModal}
        onClose={() => !isExporting && setShowExportModal(false)}
        title="Exportar Backup"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Selecione quais dados deseja exportar. O arquivo será baixado em formato JSON.
          </p>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.orders}
                onChange={(e) => setExportOptions({ ...exportOptions, orders: e.target.checked })}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">Pedidos</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.clients}
                onChange={(e) => setExportOptions({ ...exportOptions, clients: e.target.checked })}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">Clientes</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions.products}
                onChange={(e) => setExportOptions({ ...exportOptions, products: e.target.checked })}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">Produtos</span>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowExportModal(false)}
              disabled={isExporting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExportBackup}
              disabled={isExporting || (!exportOptions.orders && !exportOptions.clients && !exportOptions.products)}
            >
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Importação */}
      <Modal
        isOpen={showImportModal}
        onClose={() => !isImporting && setShowImportModal(false)}
        title="Importar Backup"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Selecione um arquivo JSON de backup para importar. <strong className="text-amber-600">ATENÇÃO:</strong> Esta ação irá sobrescrever todos os dados atuais.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Arquivo JSON
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleImportFileChange}
                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
              />
              {importFile && (
                <p className="mt-1 text-xs text-slate-500">Arquivo selecionado: {importFile.name}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportData(null);
                setImportPassword('');
              }}
              disabled={isImporting}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => setShowImportConfirm(true)}
              disabled={!importFile || isImporting}
            >
              Continuar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Confirmação de Importação */}
      <Modal
        isOpen={showImportConfirm}
        onClose={() => !isImporting && setShowImportConfirm(false)}
        title="Confirmar Importação"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm font-semibold text-amber-900 mb-2">⚠️ Atenção</p>
            <p className="text-sm text-amber-800">
              Esta ação irá <strong>apagar todos os dados atuais</strong> e substituí-los pelos dados do arquivo importado.
              Um backup automático será criado antes da importação.
            </p>
          </div>
          {importData && (
            <div className="text-sm text-slate-600 space-y-1">
              <p><strong>Versão do backup:</strong> {importData.version}</p>
              <p><strong>Exportado em:</strong> {new Date(importData.exportedAt).toLocaleString('pt-BR')}</p>
              {importData.orders && <p><strong>Pedidos:</strong> {importData.orders.length}</p>}
              {importData.clients && <p><strong>Clientes:</strong> {importData.clients.length}</p>}
              {importData.products && <p><strong>Produtos:</strong> {importData.products.length}</p>}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Digite sua senha para confirmar
            </label>
            <Input
              type="password"
              placeholder="Sua senha"
              value={importPassword}
              onChange={(e) => setImportPassword(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowImportConfirm(false);
                setImportPassword('');
              }}
              disabled={isImporting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleImportBackup}
              disabled={!importPassword || isImporting}
            >
              {isImporting ? 'Importando...' : 'Confirmar Importação'}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

